import { navigationPlayerIdAtom } from '@/atoms/navigation';
import { userNavOverridesAtom } from '@/atoms/queries/user';
import { ErrorBoundary } from '@sentry/react';
import { Button, Typography } from '@statsbomb/kitbag-components';
import { useAtomValue } from 'jotai';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const EmptyPlaylistContentBase = ({
  link,
  text,
  isPending = false,
}: {
  link?: string;
  text?: string;
  isPending?: boolean;
}) => {
  const {
    t,
    i18n: { language: locale },
  } = useTranslation('videoClient');

  return (
    <>
      <Typography variant="headingLarge">{t('noPlaylists')}</Typography>
      <Link to={link || `/${locale}/data-locker/events`}>
        <Button isPending={isPending} as="span" size="regular">
          {text || t('goToEventsDataLocker')}
        </Button>
      </Link>
    </>
  );
};

const EmptyPlaylistContentWithData = () => {
  const {
    t,
    i18n: { language: locale },
  } = useTranslation('videoClient');

  const { datahubId } = useAtomValue(navigationPlayerIdAtom);
  const userNavOverrides = useAtomValue(userNavOverridesAtom);

  const isNewPlayerXP = !userNavOverrides.player;
  const isNewPlayerScoutXP = !userNavOverrides.playerScout;

  const getButtonProps = () => {
    if (isNewPlayerXP) {
      return {
        text: t('goToPlayerDashboard'),
        link: `/${locale}/player/${datahubId}/`,
      };
    }

    if (isNewPlayerScoutXP)
      return {
        text: t('goToPlayerDataLocker'),
        link: `/${locale}/data-locker/players`,
      };

    /* undefined is handled by the base component */
    /* ignoring as can't find a nice way to test this part
    since the default is already tested */
    /* istanbul ignore next */
    return {
      text: undefined,
      link: undefined,
    };
  };

  const { text, link } = getButtonProps();

  return <EmptyPlaylistContentBase {...{ text, link }} />;
};

export const EmptyPlaylistContent = () => (
  <ErrorBoundary fallback={<EmptyPlaylistContentBase />}>
    <Suspense fallback={<EmptyPlaylistContentBase isPending />}>
      <EmptyPlaylistContentWithData />
    </Suspense>
  </ErrorBoundary>
);
